import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Loader2, Upload, Video, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function uploadAdminImage({ file, folder, token, uploadType = 'image' }) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  formData.append('upload_type', uploadType);

  const response = await axios.post(`${API}/admin/upload`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

function inferMediaKind(fileType = '', url = '') {
  if (fileType?.startsWith('video/')) return 'video';
  const normalizedUrl = String(url || '').toLowerCase();
  if (normalizedUrl.endsWith('.mp4') || normalizedUrl.endsWith('.mov') || normalizedUrl.endsWith('.webm')) {
    return 'video';
  }
  return 'image';
}

function validateFile(file, uploadType = 'image') {
  if (uploadType === 'media') {
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    const isAllowedImage = allowedImageTypes.includes(file?.type);
    const isAllowedVideo = allowedVideoTypes.includes(file?.type);

    if (!isAllowedImage && !isAllowedVideo) {
      toast.error('Please select JPG, JPEG, PNG, MP4, MOV, or WebM');
      return false;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File must be less than 50MB');
      return false;
    }

    return true;
  }

  if (uploadType === 'video') {
    if (!file?.type?.startsWith('video/')) {
      toast.error('Please select a video file');
      return false;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be less than 50MB');
      return false;
    }
    return true;
  }

  if (!file?.type?.startsWith('image/')) {
    toast.error('Please select an image file');
    return false;
  }
  if (file.size > 10 * 1024 * 1024) {
    toast.error('Image must be less than 10MB');
    return false;
  }
  return true;
}

export const ImageUploader = ({
  token,
  folder = 'uploads',
  onUpload,
  currentImage = null,
  currentMediaType = null,
  aspectRatio = 'aspect-video',
  label = 'Upload Image',
  uploadType = 'image',
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [previewType, setPreviewType] = useState(currentMediaType || inferMediaKind('', currentImage));
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(currentImage);
    setPreviewType(currentMediaType || inferMediaKind('', currentImage));
  }, [currentImage, currentMediaType]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !validateFile(file, uploadType)) return;

    const effectiveUploadType = uploadType === 'media'
      ? (file.type?.startsWith('video/') ? 'video' : 'image')
      : uploadType;

    setUploading(true);
    try {
      const result = await uploadAdminImage({ file, folder, token, uploadType: uploadType === 'media' ? 'media' : effectiveUploadType });
      const detectedType = result.type || inferMediaKind(file.type, result.url);
      setPreview(result.url);
      setPreviewType(detectedType);
      onUpload(result.url, result.storage_path || result.filename || null, detectedType);
      toast.success(detectedType === 'video' ? 'Video uploaded successfully' : 'Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setPreviewType(uploadType === 'video' ? 'video' : 'image');
    onUpload(null, null, null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileAccept = uploadType === 'video'
    ? 'video/mp4,video/webm,video/quicktime'
    : uploadType === 'media'
      ? 'image/jpeg,image/jpg,image/png,video/mp4,video/webm,video/quicktime'
      : 'image/*';

  const isVideoPreview = uploadType === 'video' || previewType === 'video';

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={fileAccept}
        onChange={handleFileSelect}
        className="hidden"
        id={`file-upload-${folder}`}
      />

      {preview ? (
        <div className={`relative ${aspectRatio} bg-stone-100 rounded-sm overflow-hidden`}>
          {isVideoPreview ? (
            <video src={preview} className="w-full h-full object-cover" controls data-testid={`media-preview-video-${folder}`} />
          ) : (
            <img src={preview} alt="Preview" className="w-full h-full object-cover" data-testid={`media-preview-image-${folder}`} />
          )}
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 rounded-full"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <label
          htmlFor={`file-upload-${folder}`}
          className={`flex flex-col items-center justify-center ${aspectRatio} bg-stone-50 border-2 border-dashed border-stone-200 rounded-sm cursor-pointer hover:border-[#5C0120] hover:bg-stone-100 transition-colors duration-200`}
          data-testid={`media-upload-label-${folder}`}
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
          ) : (
            <>
              {uploadType === 'video' ? (
                <Video className="h-8 w-8 text-stone-400 mb-2" />
              ) : uploadType === 'media' ? (
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="h-7 w-7 text-stone-400" />
                  <Video className="h-7 w-7 text-stone-400" />
                </div>
              ) : (
                <ImageIcon className="h-8 w-8 text-stone-400 mb-2" />
              )}
              <span className="text-sm text-stone-500">{label}</span>
              <span className="text-xs text-stone-400 mt-1">
                {uploadType === 'video' || uploadType === 'media' ? 'Max 50MB' : 'Max 10MB'}
              </span>
            </>
          )}
        </label>
      )}
    </div>
  );
};

export const MultiImageUploader = ({
  token,
  folder = 'uploads',
  onUpload,
  currentImages = [],
  maxImages = 5,
  label = 'Upload Images',
}) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(currentImages);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    if (!validateFile(file)) return;

    setUploading(true);
    try {
      const result = await uploadAdminImage({ file, folder, token });
      const newImages = [...images, result.url];
      setImages(newImages);
      onUpload(newImages);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onUpload(newImages);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, index) => (
          <div key={index} className="relative aspect-video bg-stone-100 rounded-sm overflow-hidden">
            <img src={img} alt={`Image ${index + 1}`} className="w-full h-full object-cover" />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 rounded-full h-7 w-7"
              onClick={() => handleRemove(index)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}

        {images.length < maxImages && (
          <label
            htmlFor={`multi-file-upload-${folder}`}
            className="flex flex-col items-center justify-center aspect-video bg-stone-50 border-2 border-dashed border-stone-200 rounded-sm cursor-pointer hover:border-[#5C0120] hover:bg-stone-100 transition-colors duration-200"
          >
            {uploading ? (
              <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
            ) : (
              <>
                <Upload className="h-8 w-8 text-stone-400 mb-2" />
                <span className="text-sm text-stone-500">{label}</span>
                <span className="text-xs text-stone-400 mt-1">{images.length}/{maxImages}</span>
              </>
            )}
          </label>
        )}
      </div>

      <input
        ref={fileInputRef}
        id={`multi-file-upload-${folder}`}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};
